/**
 * Disponibilità derivata per esperienze in availability_mode='derived'.
 *
 * Le partenze NON sono materializzate: si derivano dalle fasce del periodo
 * (seasonal_periods, o default_time_slots come fallback) per ogni data, meno i
 * blocchi (eccezioni del vendor). Le partenze già prenotate (slot materializzati
 * con booked_count>0) restano sempre disponibili anche se la fascia è stata
 * cambiata o bloccata ("sticky": un cambio fasce vale solo avanti e non tocca
 * le prenotazioni esistenti).
 *
 * Pure: importa solo excursion-seasonal-slots → usabile client e server.
 */
import { slotsForDate, type SeasonalPeriod, type SeasonalSlot } from './excursion-seasonal-slots';

export interface ExcBlock {
  start_date: string; // 'YYYY-MM-DD'
  end_date: string;   // 'YYYY-MM-DD'
  start_time: string | null; // 'HH:MM' o null (= tutte le fasce)
  seats_blocked?: number | null; // null = blocco PIENO; >0 = N posti bloccati (parziale)
}

export interface MaterializedSlot {
  id: string;
  start_time: string; // 'HH:MM'
  booked_count: number;
  capacity: number | null;
  status: string;
}

export interface DerivedDeparture {
  start_time: string;
  duration_minutes: number;
  capacity: number | null;
  booked_count: number;
  blocked: boolean;       // fascia bloccata PIENA (mostrata solo se già prenotata)
  blocked_seats: number;  // posti bloccati parzialmente (telefonico/iCal)
  booked: boolean;        // booked_count > 0
  slot_id?: string;       // presente se materializzato
}

function inRange(dateStr: string, start: string, end: string): boolean {
  // 'YYYY-MM-DD' confronto lessicografico = cronologico
  return dateStr >= start && dateStr <= end;
}

/** True se la fascia (data+ora) è coperta da un blocco PIENO (non parziale). */
export function isFasciaBlocked(blocks: ExcBlock[], dateStr: string, time: string): boolean {
  for (const b of blocks) {
    if (b.seats_blocked != null) continue; // blocco parziale → non nasconde la fascia
    if (inRange(dateStr, b.start_date, b.end_date) && (b.start_time == null || b.start_time === time)) {
      return true;
    }
  }
  return false;
}

/** Posti bloccati parzialmente (blocchi con seats_blocked>0) su una fascia. */
export function blockedSeatsForFascia(blocks: ExcBlock[], dateStr: string, time: string): number {
  let sum = 0;
  for (const b of blocks) {
    if (b.seats_blocked == null || b.seats_blocked <= 0) continue;
    if (inRange(dateStr, b.start_date, b.end_date) && (b.start_time == null || b.start_time === time)) {
      sum += b.seats_blocked;
    }
  }
  return sum;
}

/** True se l'intero giorno è bloccato PIENO (blocco senza orario e non parziale). */
export function isDayFullyBlocked(blocks: ExcBlock[], dateStr: string): boolean {
  return blocks.some((b) => b.start_time == null && b.seats_blocked == null && inRange(dateStr, b.start_date, b.end_date));
}

/**
 * Partenze derivate per una data:
 *   fasce correnti del periodo − blocchi  ∪  partenze già prenotate (sticky).
 */
export function derivedDeparturesForDate(args: {
  dateStr: string;
  periods: SeasonalPeriod[];
  flat: SeasonalSlot[];
  blocks: ExcBlock[];
  capacity: number | null;
  materialized: MaterializedSlot[];
  /** Posti PRENOTABILI (max_guests ?? max_capacity). Se non null, cappa la capacity
   *  dello slot materializzato (che può contenere la capienza legale > bookable). */
  bookableCap?: number | null;
  /**
   * default false (comportamento invariato per /available-dates pubblico e per la
   * validazione in fase di booking: una fascia bloccata PIENA e mai prenotata da noi
   * resta invisibile/non prenotabile). true → SOLO calendario vendor: la fascia bloccata
   * piena (es. canale esterno come Holidoit su un'esperienza "a gruppo") resta VISIBILE
   * — il vendor deve poter vedere CHI l'ha occupata, anche se non può modificarla da qui.
   */
  includeBlockedUnbooked?: boolean;
}): DerivedDeparture[] {
  const { dateStr, periods, flat, blocks, capacity, materialized, bookableCap, includeBlockedUnbooked } = args;
  const md = dateStr.slice(5); // 'MM-DD'
  const fasce = periods.length > 0 ? slotsForDate(periods, md) : flat;
  const matByTime = new Map(materialized.map((m) => [m.start_time, m]));
  const out: DerivedDeparture[] = [];
  const seen = new Set<string>();

  for (const f of fasce) {
    seen.add(f.start_time);
    const mat = matByTime.get(f.start_time);
    const booked = (mat?.booked_count || 0) > 0;
    const blocked = isFasciaBlocked(blocks, dateStr, f.start_time);
    // Fascia bloccata PIENA e NON prenotata → non compare (salvo includeBlockedUnbooked).
    if (blocked && !booked && !includeBlockedUnbooked) continue;
    const eff = mat?.capacity ?? capacity;
    out.push({
      start_time: f.start_time,
      duration_minutes: f.duration_minutes,
      capacity: (bookableCap != null && eff != null) ? Math.min(eff, bookableCap) : eff,
      booked_count: mat?.booked_count || 0,
      blocked,
      blocked_seats: blockedSeatsForFascia(blocks, dateStr, f.start_time),
      booked,
      slot_id: mat?.id,
    });
  }

  // Partenze prenotate non più presenti nelle fasce correnti → restano (sticky).
  for (const m of materialized) {
    if (seen.has(m.start_time) || (m.booked_count || 0) <= 0) continue;
    const effSticky = m.capacity ?? capacity;
    out.push({
      start_time: m.start_time,
      duration_minutes: 0,
      capacity: (bookableCap != null && effSticky != null) ? Math.min(effSticky, bookableCap) : effSticky,
      booked_count: m.booked_count,
      blocked: false,
      blocked_seats: blockedSeatsForFascia(blocks, dateStr, m.start_time),
      booked: true,
      slot_id: m.id,
    });
  }

  out.sort((a, b) => a.start_time.localeCompare(b.start_time));
  return out;
}

/** Helper: 'HH:MM' da un ISO start_at. */
export function timeOf(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
